import {
  deserializeInviteFormStored,
  hasMeaningfulInviteFormState,
  serializeInviteFormState,
  type InviteFormState,
  type InviteFormStored,
} from './invite-form-schema';

const SELECTED_EMPLOYEES_KEY = 'selectedEmployees';
const INVITE_FORM_DATA_KEY = 'inviteFormData';

type StoredCheckedUsers = Record<string, number>;

export interface SelectedEmployeesData {
  eventId: number;
  checkedUsers: Record<number, number>;
}

export type { InviteFormState, InviteFormStored };

export const normalizeCheckedUsers = (raw: StoredCheckedUsers | Record<number, number>): Record<number, number> =>
  Object.fromEntries(Object.entries(raw).map(([ticketId, userId]) => [Number(ticketId), Number(userId)]));

const parseJson = <T>(value: string | null): T | null => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const isMatchingEvent = <T extends { eventId: number }>(data: T | null, eventId: number): data is T =>
  data !== null && data.eventId === eventId;

const normalizeStoredCheckedUsers = (value: unknown): Record<number, number> | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const entries = Object.entries(value);
  if (entries.some(([key, userId]) => Number.isNaN(Number(key)) || Number.isNaN(Number(userId)))) {
    return null;
  }

  return normalizeCheckedUsers(value as StoredCheckedUsers);
};

export const loadSelectedEmployees = (eventId: number): SelectedEmployeesData | null => {
  try {
    const data = parseJson<{ eventId: number; checkedUsers: unknown }>(sessionStorage.getItem(SELECTED_EMPLOYEES_KEY));
    const checkedUsers = data && isMatchingEvent(data, eventId) ? normalizeStoredCheckedUsers(data.checkedUsers) : null;

    if (!checkedUsers) {
      return null;
    }

    return {
      eventId,
      checkedUsers,
    };
  } catch {
    return null;
  }
};

export const saveSelectedEmployees = (data: SelectedEmployeesData): void => {
  try {
    sessionStorage.setItem(SELECTED_EMPLOYEES_KEY, JSON.stringify(data));
  } catch {
    console.error('Ошибка сохранения выбранных участников');
  }
};

export const clearSelectedEmployees = (): void => {
  try {
    sessionStorage.removeItem(SELECTED_EMPLOYEES_KEY);
  } catch {
    console.error('Ошибка очистки выбранных участников');
  }
};

export const loadInviteFormData = (eventId: number): InviteFormState | null => {
  try {
    const raw = parseJson<unknown>(sessionStorage.getItem(INVITE_FORM_DATA_KEY));
    return deserializeInviteFormStored(raw, eventId);
  } catch {
    return null;
  }
};

export const saveInviteFormData = (eventId: number, state: InviteFormState): void => {
  try {
    sessionStorage.setItem(INVITE_FORM_DATA_KEY, JSON.stringify(serializeInviteFormState(eventId, state)));
  } catch {
    console.error('Ошибка сохранения формы приглашения');
  }
};

export const clearInviteFormData = (): void => {
  try {
    sessionStorage.removeItem(INVITE_FORM_DATA_KEY);
  } catch {
    console.error('Ошибка очистки формы приглашения');
  }
};

export const clearAllInviteSessionData = (): void => {
  clearSelectedEmployees();
  clearInviteFormData();
};

export const hasMeaningfulInviteFormData = (saved: InviteFormState | null): boolean =>
  hasMeaningfulInviteFormState(saved);

export { parseStoredDate, serializeDate } from './invite-form-schema';
